import React, { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import { Save, Youtube, Clapperboard, Music2, Instagram, Search, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const monthNow = () => new Date().toISOString().slice(0, 7);
const PLATFORMS = [
  { key: "youtube", label: "YouTube Video", icon: Youtube, color: "#DC2626" },
  { key: "short", label: "Shorts", icon: Clapperboard, color: "#D97706" },
  { key: "tiktok", label: "TikTok", icon: Music2, color: "#111113" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#7C3AED" },
];

const StatCard = ({ icon: Icon, label, value, color, testid }) => (
  <div className="bg-white border border-[#E4E4E7] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} style={{ color }} />
      <div className="label-cap text-[10px]">{label}</div>
    </div>
    <div className="font-heading text-2xl sm:text-3xl font-black tracking-tighter">{value}</div>
  </div>
);

const inputCls = "w-full px-2 py-1.5 border border-[#E4E4E7] text-xs font-mono outline-none focus:border-[#002FA7]";

const Sosmed = () => {
  const [fromMonth, setFromMonth] = useState(monthNow());
  const [toMonth, setToMonth] = useState(monthNow());
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [staffs, setStaffs] = useState([]);
  const [savingId, setSavingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sosmed", { params: { start: `${fromMonth}-01`, end: `${toMonth}-31` } });
      setItems(data.items || []);
      setTotals(data.totals || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/technicians").then((r) => setStaffs((r.data || []).filter((t) => (t.role || "teknisi") === "staff")));
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromMonth, toMonth]);

  const setRow = (id, patch) => setItems((rs) => rs.map((r) => (r.order_id === id ? { ...r, ...patch, _dirty: true } : r)));

  const saveRow = async (r) => {
    setSavingId(r.order_id);
    try {
      const { data: res } = await api.put(`/sosmed/${r.order_id}`, {
        staff_youtube_id: r.staff_youtube_id || "",
        staff_short_id: r.staff_short_id || "",
        youtube: r.youtube || "", short: r.short || "", tiktok: r.tiktok || "", instagram: r.instagram || "",
      });
      const dups = res.duplicates || [];
      const cleared = {};
      dups.forEach((d) => { cleared[d.field] = ""; });
      setItems((rs) => rs.map((x) => (x.order_id === r.order_id ? { ...x, ...cleared, _dirty: false } : x)));
      const { data } = await api.get("/sosmed", { params: { start: `${fromMonth}-01`, end: `${toMonth}-31` } });
      setTotals(data.totals || {});
      if (dups.length > 0) {
        const labelOf = (k) => (PLATFORMS.find((p) => p.key === k)?.label || k);
        toast.error(`⚠️ Konten duplikat — ${dups.map((d) => `${labelOf(d.field)} sudah pernah diinput di ${d.order_no}`).join("; ")}. Input tersebut dikosongkan.`, { duration: 9000 });
      } else {
        toast.success(`Content ${r.order_no} tersimpan`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSavingId("");
    }
  };

  const downloadExcel = () => {
    const token = localStorage.getItem("om_token");
    window.open(`${API}/sosmed/export.xlsx?start=${fromMonth}-01&end=${toMonth}-31&token=${encodeURIComponent(token || "")}`, "_blank");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      (r.customer_name || "").toLowerCase().includes(q) ||
      (r.order_no || "").toLowerCase().includes(q) ||
      (r.city || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const filled = useMemo(() => items.filter((r) => PLATFORMS.some((p) => (r[p.key] || "").trim())).length, [items]);

  const StaffSelect = ({ value, onChange, testid }) => (
    <select value={value || ""} onChange={onChange} data-testid={testid} className={inputCls}>
      <option value="">— Pilih Staff —</option>
      {staffs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );

  return (
    <Layout>
      <div className="mb-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <div className="label-cap mb-2">Dokumentasi & Publikasi</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-black tracking-tight">Content Sosmed</h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <div className="label-cap text-[10px] mb-1">Dari Bulan</div>
            <input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} data-testid="sosmed-from-month" className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7]" />
          </div>
          <div>
            <div className="label-cap text-[10px] mb-1">Sampai Bulan</div>
            <input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} data-testid="sosmed-to-month" className="px-3 py-2 border border-[#E4E4E7] text-sm font-mono outline-none focus:border-[#002FA7]" />
          </div>
          <button onClick={downloadExcel} data-testid="sosmed-export-excel" className="bg-[#059669] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 flex items-center gap-2 hover:bg-[#047857]">
            <FileSpreadsheet size={14} /> Excel
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {PLATFORMS.map((p) => (
          <StatCard key={p.key} icon={p.icon} label={p.label} value={totals[p.key] || 0} color={p.color} testid={`sosmed-stat-${p.key}`} />
        ))}
      </div>

      <div className="bg-white border border-[#E4E4E7]">
        <div className="px-4 py-3 border-b border-[#E4E4E7] flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <span className="label-cap">Order ({filtered.length}{search ? ` / ${items.length}` : ""})</span>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari client / order / kota…" data-testid="sosmed-search" className="pl-8 pr-3 py-1.5 border border-[#E4E4E7] text-xs outline-none focus:border-[#002FA7] w-56" />
            </div>
            <span className="text-xs text-[#52525B] whitespace-nowrap">{filled} ada content{loading ? " · memuat…" : ""}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sosmed-table">
            <thead>
              <tr className="text-left border-b border-[#E4E4E7]">
                <th className="px-3 py-2 label-cap text-[10px]">Order</th>
                <th className="px-3 py-2 label-cap text-[10px]">Client</th>
                <th className="px-3 py-2 label-cap text-[10px]">Kota</th>
                <th className="px-3 py-2 label-cap text-[10px]">Staff YT Video</th>
                <th className="px-3 py-2 label-cap text-[10px]">YouTube Video</th>
                <th className="px-3 py-2 label-cap text-[10px]">Staff Short/TT/IG</th>
                <th className="px-3 py-2 label-cap text-[10px]">Short</th>
                <th className="px-3 py-2 label-cap text-[10px]">TikTok</th>
                <th className="px-3 py-2 label-cap text-[10px]">Instagram</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[#52525B] text-sm">{search ? "Tidak ada hasil pencarian." : "Tidak ada order pada rentang bulan ini."}</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.order_id} className="border-b border-[#E4E4E7]/60 last:border-0 align-top" data-testid={`sosmed-row-${r.order_no}`}>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="font-mono font-bold text-xs">{r.order_no}</div>
                    <div className="text-[10px] text-[#A1A1AA]">{r.schedule_date}</div>
                  </td>
                  <td className="px-3 py-2 text-xs min-w-[100px]">{r.customer_name || "-"}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.city || "-"}</td>
                  <td className="px-3 py-2 min-w-[120px]">
                    <StaffSelect value={r.staff_youtube_id} onChange={(e) => setRow(r.order_id, { staff_youtube_id: e.target.value })} testid={`sosmed-staff-yt-${r.order_no}`} />
                  </td>
                  <td className="px-3 py-2 min-w-[140px]">
                    <input value={r.youtube || ""} onChange={(e) => setRow(r.order_id, { youtube: e.target.value })} placeholder="https://…" data-testid={`sosmed-youtube-${r.order_no}`} className={inputCls} />
                    {(r.youtube || "").trim() && <a href={r.youtube} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline">buka link ↗</a>}
                  </td>
                  <td className="px-3 py-2 min-w-[120px]">
                    <StaffSelect value={r.staff_short_id} onChange={(e) => setRow(r.order_id, { staff_short_id: e.target.value })} testid={`sosmed-staff-short-${r.order_no}`} />
                  </td>
                  {["short", "tiktok", "instagram"].map((p) => (
                    <td key={p} className="px-3 py-2 min-w-[140px]">
                      <input value={r[p] || ""} onChange={(e) => setRow(r.order_id, { [p]: e.target.value })} placeholder="https://…" data-testid={`sosmed-${p}-${r.order_no}`} className={inputCls} />
                      {(r[p] || "").trim() && <a href={r[p]} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline">buka link ↗</a>}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <button onClick={() => saveRow(r)} disabled={savingId === r.order_id} data-testid={`sosmed-save-${r.order_no}`}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${r._dirty ? "bg-[#002FA7] text-white" : "border border-[#E4E4E7] text-[#52525B]"} disabled:opacity-60`}>
                      <Save size={12} /> Simpan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Sosmed;
